import apiService from './api.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface VatReturnRow {
  id: string;
  return_number: string;
  period_from: string;
  period_to: string;
  status: string;
  trn_snapshot?: string;
  net_vat_due: number;
  output_vat_total: number;
  input_vat_total: number;
  settlement_journal_voucher_id?: string;
  posted_at?: string;
  notes?: string;
  lines?: Array<{
    id: string;
    box_code: string;
    label: string;
    amount_aed: number;
  }>;
  validation_warnings_json?: string[];
  validation_errors_json?: string[];
  sourceLinks?: Array<{
    id: string;
    source_type: string;
    source_id: string;
    amount_aed: number;
    side: string;
    meta_json?: Record<string, unknown>;
  }>;
}

const client = () => apiService.getClient();

export async function listVatReturns(): Promise<VatReturnRow[]> {
  const res = await client().get('/finance/vat-returns');
  return unwrap<VatReturnRow[]>(res);
}

export async function getVatReturn(id: string): Promise<VatReturnRow> {
  const res = await client().get(`/finance/vat-returns/${id}`);
  return unwrap<VatReturnRow>(res);
}

export async function createVatReturnDraft(body: {
  period_from: string;
  period_to: string;
  trn_snapshot?: string;
  notes?: string;
}): Promise<VatReturnRow> {
  const res = await client().post('/finance/vat-returns', body);
  return unwrap<VatReturnRow>(res);
}

export async function calculateVatReturn(id: string): Promise<VatReturnRow> {
  const res = await client().post(`/finance/vat-returns/${id}/calculate`);
  return unwrap<VatReturnRow>(res);
}

export async function validateVatReturn(id: string): Promise<VatReturnRow> {
  const res = await client().post(`/finance/vat-returns/${id}/validate`);
  return unwrap<VatReturnRow>(res);
}

export async function saveVatReturnNotes(id: string, notes: string): Promise<VatReturnRow> {
  const res = await client().put(`/finance/vat-returns/${id}`, { notes });
  return unwrap<VatReturnRow>(res);
}

export interface VatClearingLine {
  account_id: string;
  account_code: string;
  account_name: string;
  side: 'output' | 'input';
  amount: number;
  documents: number;
}

export interface VatSettlementSetup {
  return_id: string;
  return_number: string;
  status: string;
  base_currency: string;
  output_total: number;
  input_total: number;
  net: number;
  direction: 'payable' | 'refundable' | 'nil';
  clearing: VatClearingLine[];
  unresolved: string[];
  stale: boolean;
  fta_supplier: { id: string; name: string } | null;
  vat_payable_account: { id: string; code: string; name: string } | null;
  department_id: string | null;
  suggested_account: { account_code: string; account_name: string; parent_id: string | null; parent_label: string | null };
  bill_dates: { invoice_date: string; due_date: string };
}

export interface VatSettlementPostInput {
  fta_supplier_id?: string;
  vat_payable_account_id?: string;
  create_payable_account?: { account_code: string; account_name: string; parent_id?: string | null };
  department_id?: string;
  remember?: boolean;
}

export interface VatSettlementStatus {
  journal_voucher: { id: string; voucher_number: string } | null;
  bill: {
    id: string;
    invoice_number: string;
    supplier_name: string;
    grand_total: number;
    paid_amount: number;
    balance_due: number;
    payment_status: string;
    due_date: string | null;
  } | null;
  payments: Array<{ id: string; voucher_number: string; date: string; status: string; amount: number }>;
}

export async function getVatSettlementSetup(id: string): Promise<VatSettlementSetup> {
  const res = await client().get(`/finance/vat-returns/${id}/settlement-setup`);
  return unwrap<VatSettlementSetup>(res);
}

export async function getVatSettlementStatus(id: string): Promise<VatSettlementStatus> {
  const res = await client().get(`/finance/vat-returns/${id}/settlement`);
  return unwrap<VatSettlementStatus>(res);
}

export async function postVatReturn(id: string, body: VatSettlementPostInput): Promise<VatReturnRow> {
  const res = await client().post(`/finance/vat-returns/${id}/post`, body);
  return unwrap<VatReturnRow>(res);
}

export async function fetchVatReturnExcelBlob(id: string): Promise<Blob> {
  const res = await client().get(`/finance/vat-returns/${id}/export/excel`, {
    responseType: 'blob',
  });
  return res.data as Blob;
}